import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./SummerSalePeek.module.scss";
import saleSticker from "images/heros/Summer Sale/summer-peek-sticker-open.png";
import saleSticker2x from "images/heros/Summer Sale/summer-peek-sticker-open@2x.png";
import { SUMMER_SALE, isSummerSaleActive } from "constants/SummerSale";

/**
 * Floating "peek" tab pinned to the right edge of the viewport. It appears once
 * the full-width SummerSaleBanner (marked with data-summer-sale-banner) has
 * scrolled out of view, showing just the lolly sticker poking out. Hovering
 * slides the whole tab in to reveal the offer; clicking goes to the shop.
 *
 * Mounted once globally (Layout) so it tracks the banner across page scrolls.
 */
const SummerSalePeek = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [visible, setVisible] = useState(false);

    // Only show the floating peek on the home page (hidden on mobile via CSS).
    const isHomePage = location.pathname === "/";

    useEffect(() => {
        if (!isSummerSaleActive() || !isHomePage) return undefined;

        let raf = null;
        const evaluate = () => {
            raf = null;
            const banner = document.querySelector("[data-summer-sale-banner]");
            if (!banner) {
                // No banner on this page → nothing to "scroll past", keep hidden.
                setVisible(false);
                return;
            }
            // Show once the banner's bottom edge has scrolled above the viewport.
            setVisible(banner.getBoundingClientRect().bottom <= 0);
        };
        const onScroll = () => {
            if (raf == null) raf = window.requestAnimationFrame(evaluate);
        };

        // Re-check after the new route has painted, then on scroll/resize.
        const t = window.setTimeout(evaluate, 50);
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.clearTimeout(t);
            if (raf != null) window.cancelAnimationFrame(raf);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [location.pathname, isHomePage]);

    if (!isSummerSaleActive() || !isHomePage) return null;

    const goToShop = () => navigate("/shop");

    return (
        <div
            className={`${styles.peek} ${visible ? styles.visible : ""}`}
            onClick={goToShop}
            role="button"
            tabIndex={visible ? 0 : -1}
            aria-hidden={!visible}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToShop()}
            aria-label={`${SUMMER_SALE.percent}% off summer sale — shop now`}
        >
            <div className={styles.panel}>
                <div className={styles.body}>
                    <span className={styles.percent}>{SUMMER_SALE.percent}% OFF</span>
                    <span className={styles.cta}>Grab your deal now! &rsaquo;</span>
                </div>
                <div className={styles.bars} aria-hidden="true">
                    <span /><span /><span /><span />
                </div>
            </div>
            <img
                src={saleSticker}
                srcSet={`${saleSticker} 1x, ${saleSticker2x} 2x`}
                className={styles.sticker}
                alt="Summer Sale"
                draggable={false}
            />
        </div>
    );
};

export default SummerSalePeek;
